/* ---------- constants ---------- */
const H_SPACING = 350;
const V_SPACING = 150;

const MARGIN_X = 10;
const MARGIN_Y = 750;

/* ---------- GRAPH BUILDING ---------- */
export function buildGraph(raw) {
  const nodes = (raw.nodes || []).map((n) => {
    const baseWidth = 218;
    const baseHeight = 75;

    const subtitleLower = (n.subtitle || '').toLowerCase();

    // Rhombus nodes (kept for backwards compat if any explicit rhombus type)
    const isRhombus = n.type === 'rhombus';

    // Hexagon nodes: milestone, decision, gate, checkpoint subtitles
    const isHexagon = !isRhombus && (
      subtitleLower.includes('milestone') ||
      subtitleLower.includes('decision') ||
      subtitleLower.includes('gate') ||
      subtitleLower.includes('checkpoint') ||
      subtitleLower.includes('deliverable') ||
      subtitleLower.includes('review') ||
      subtitleLower.includes('approval')
    );

    let nodeWidth, nodeHeight;
    if (isRhombus) {
      nodeWidth = 278;
      nodeHeight = 138;
    } else if (isHexagon) {
      nodeWidth = n.width || 160;
      nodeHeight = n.height || 55;
    } else {
      nodeWidth = n.width || baseWidth;
      nodeHeight = n.height || baseHeight;
    }

    return {
      id: String(n.id),
      type: n.type || "rectangle",
      title: n.title || n.label || n.id,
      subtitle: n.subtitle || "",
      startDate: n.startDate || "",
      dueDate: n.dueDate || "",
      assignees: n.assignees || [],
      status: n.status || "",
      maturity: n.maturity || "",
      totalFloat: n.totalFloat || 0,
      hierarchyDepth: n.hierarchyDepth ?? 0,
      isInitial: n.isInitial ?? false,
      hasChildren: n.hasChildren ?? false,
      rawDueDate: n.rawDueDate || "",
      rawActualFinishDate: n.rawActualFinishDate || "",
      // Set larger size for rhombus nodes
      width: nodeWidth,
      height: nodeHeight,
      x: 0,
      y: 0,
    };
  });

  const nodeById = Object.fromEntries(nodes.map((n) => [n.id, n]));

  const edges = (raw.edges || []).map((e) => ({
    from: String(e.from),
    to: String(e.to),
    type: e.type || "orthogonal",
    color: e.color || null,
    label: e.label || null,
    labelColor: e.labelColor || "#333",
  }));

  const adj = {};
  nodes.forEach((n) => (adj[n.id] = []));
  edges.forEach(
    (e) => nodeById[e.from] && nodeById[e.to] && adj[e.from].push(e.to)
  );

  return { nodes, nodeById, edges, adj };
}

/* ---------- TOPOLOGICAL SORT ---------- */
function topologicalSort(nodes, adj) {
  const indeg = {};

  nodes.forEach((n) => (indeg[n.id] = 0));

  for (const u in adj) adj[u].forEach((v) => indeg[v]++);

  const q = [];
  nodes.forEach((n) => indeg[n.id] === 0 && q.push(n.id));

  const topo = [];

  while (q.length) {
    const u = q.shift();
    topo.push(u);

    (adj[u] || []).forEach((v) => {
      indeg[v]--;
      if (indeg[v] === 0) q.push(v);
    });
  }

  // Handle cycles.
  if (topo.length !== nodes.length) {
    nodes
      .map((n) => n.id)
      .filter((id) => !topo.includes(id))
      .forEach((id) => topo.push(id));
  }

  return topo;
}

/* ---------- COMPUTE LEVELS ---------- */
export function computeLevels(nodes, adj) {
  const topo = topologicalSort(nodes, adj);
  const level = {};

  nodes.forEach((n) => (level[n.id] = 0));

  for (const id of topo) {
    (adj[id] || []).forEach((v) => {
      if (level[v] < level[id] + 1) {
        level[v] = level[id] + 1;
      }
    });
  }

  return level;
}

/* ---------- ASSIGN POSITIONS ---------- */
export function assignPositions(nodes, levelMap) {
  const buckets = {};

  nodes.forEach((n) => {
    const lv = levelMap[n.id] ?? 0;
    (buckets[lv] ??= []).push(n);
    n.level = lv;
  });

  const maxCount = Math.max(
    ...Object.values(buckets).map((arr) => arr.length),
    1
  );

  Object.entries(buckets).forEach(([lvStr, arr]) => {
    const lv = Number(lvStr);

    const colSz = arr.length;
    const colH = (maxCount - 1) * V_SPACING;
    const thisH = (colSz - 1) * V_SPACING;

    const offsetY = MARGIN_Y + (colH - thisH) / 2;

    arr.forEach((node, i) => {
      node.x = MARGIN_X + lv * H_SPACING;
      node.y = offsetY + i * V_SPACING;
    });
  });
}

/* ---------- TIMELINE POSITIONS ---------- */
export function assignTimelinePositions(nodes, sortByField = "startDate") {
  const buckets = {};
  let minTime = Infinity;

  nodes.forEach((n) => {
    const dateStr = n[sortByField];
    if (dateStr) {
      const time = new Date(dateStr).getTime();
      if (!isNaN(time)) {
        minTime = Math.min(minTime, time);
      }
    }
  });

  if (minTime === Infinity) minTime = 0;

  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

  nodes.forEach((n) => {
    const dateStr = n[sortByField];
    let weekIndex = 0;
    if (dateStr) {
      const time = new Date(dateStr).getTime();
      if (!isNaN(time)) {
        weekIndex = Math.floor((time - minTime) / MS_PER_WEEK);
      }
    }
    (buckets[weekIndex] ??= []).push(n);
  });

  const sortedWeeks = Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b);

  // Pre-calculate grid logic to find maxRows across the entire diagram
  const weekLayouts = sortedWeeks.map((weekIndex) => {
    const arr = buckets[weekIndex];
    arr.sort((a, b) => {
      const ta = a[sortByField] ? new Date(a[sortByField]).getTime() : 0;
      const tb = b[sortByField] ? new Date(b[sortByField]).getTime() : 0;
      return ta - tb;
    });

    // 1-3 nodes -> 1 col. 4-6 -> 2 cols. 7+ -> 3 cols.
    const cols = Math.min(3, Math.ceil(arr.length / 3));
    const rows = Math.ceil(arr.length / cols);

    return { arr, cols, rows, weekIndex };
  });

  let currentX = MARGIN_X; // Start rendering at the left margin

  weekLayouts.forEach((wl, colIndex) => {
    const { arr, cols, weekIndex } = wl;

    const SUB_W = 280; // Distance between side-by-side nodes
    // Col width is slightly wider than the grid to provide padding
    const colWidth = Math.max(350, cols * SUB_W + 40);

    const offsetY = MARGIN_Y; // Start nodes from the top, not centered

    const weekStart = new Date(minTime + weekIndex * MS_PER_WEEK);
    const weekEnd = new Date(minTime + (weekIndex + 1) * MS_PER_WEEK - 86400000);
    const opts = { month: "short", day: "numeric" };
    const rangeStr = `${weekStart.toLocaleDateString("en-US", opts)} - ${weekEnd.toLocaleDateString("en-US", opts)}`;

    const actualGridW = (cols - 1) * SUB_W + 220;
    const startX = currentX + (colWidth - actualGridW) / 2;

    arr.forEach((node, i) => {
      const r = Math.floor(i / cols);
      const c = i % cols;

      node.x = startX + c * SUB_W;
      node.y = offsetY + r * V_SPACING;
      
      node.level = colIndex; // Keep level continuous for camera logic
      node.timelineWeek = weekIndex; // Save week for timeline headers
      node.timelineRange = rangeStr;
      
      // Pass boundary metadata for TimelineHeaders
      node.timelineColLeft = currentX;
      node.timelineColWidth = colWidth;
    });

    currentX += colWidth; // Advance to the next column
  });
}

export function computeNodesBounds(nodes) {
  if (!nodes.length) return { minX: 0, minY: 0, maxX: 1200, maxY: 800 };

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  nodes.forEach((n) => {
    const nx = n.x || 0;
    const ny = n.y || 0;
    const w = n.width || 220;
    const h = n.height || 85;
    minX = Math.min(minX, nx);
    minY = Math.min(minY, ny);
    maxX = Math.max(maxX, nx + w);
    maxY = Math.max(maxY, ny + h);
  });
  
  // Provide enough padding so TimelineHeaders (which draw 100px above minNodeY) are visible
  const pad = 120;
  return {
    minX: minX - pad,
    minY: minY - pad,
    maxX: maxX + pad,
    maxY: maxY + pad,
  };
}
